import { createInstance, dateReviver } from '@/api';
import {
    ProductProcessDefinitionDependenciesReportDto,
    ProductProcessDefinitionClient,
    ProductProcessDefinitionDetailDto,
    ProductProcessDefinitionCreateDto,
    ProductProcessDefinitionUpdateDto,
    ProductProcessDefinitionCascadeDeleteOptionsDto,
    ProductProcessDefinitionExportOptionsDto,
    ImportSpecificationDto,
    ImportSpecificationType,
    ProductProcessDefinitionReportingOptionsDto
} from '@/api/backoffice/clients';
import { i18n } from '@/i18n';
import { useAlertStore } from '@/modules/core/store/alert.store';
import { defineStore } from 'pinia';
import { useProductProcessDefinitionEditStore } from './product-process-definition-edit.store';
import { useSourceSpecificationStore } from '../source-specification/source-specification.store';
import { ENUM_StatusTypes } from '../../common';
import downloadBlob from '@/helpers/download.helpers';
import { useProductSpecificationStore } from '../product-specification/product-specification.store';

export interface ProductProcessDefinitionDetailStoreState {
    detail?: ProductProcessDefinitionDetailDto;
    dependenciesReport?: ProductProcessDefinitionDependenciesReportDto;
    loading: boolean;
    reloading: boolean;
}

export const client = new ProductProcessDefinitionClient('', createInstance(), dateReviver);

export const useProductProcessDefinitionDetailStore = defineStore(
    'configuration-product-process-definition-detail-store',
    {
        state: (): ProductProcessDefinitionDetailStoreState => ({
            detail: null,
            loading: false,
            reloading: false
        }),
        getters: {
            isDraft(): boolean {
                return this.detail?.statusId === ENUM_StatusTypes.Draft.id;
            }
        },
        actions: {
            async getDetail(id: number) {
                return await client.getDetail(id);
            },
            async get(id: number, reloading: boolean = false) {
                reloading ? (this.reloading = true) : (this.loading = true);
                try {
                    this.detail = await client.getDetail(id);
                    return this.detail;
                } catch (error) {
                    useAlertStore().handleException(
                        i18n.global.t('productprocessdefinition.detail.error.get.title'),
                        error,
                        false
                    );
                } finally {
                    reloading ? (this.reloading = false) : (this.loading = false);
                }
            },
            async getDependenciesReport(id: number, reloading: boolean = false) {
                reloading ? (this.reloading = true) : (this.loading = true);
                try {
                    this.dependenciesReport = await client.getDependenciesReport(id);
                    return this.dependenciesReport;
                } catch (error) {
                    useAlertStore().handleException(
                        i18n.global.t('productprocessdefinition.detail.error.get.title'),
                        error,
                        false
                    );
                } finally {
                    reloading ? (this.reloading = false) : (this.loading = false);
                }
            },
            async changeStatus(id: number, statusType: string): Promise<boolean> {
                this.reloading = true;
                try {
                    await client.changeStatus(id, statusType);
                    useAlertStore().setSuccess(i18n.global.t('productprocessdefinition.detail.success.update.title'));
                    await this.get(id, true);
                    return true;
                } catch (error) {
                    useAlertStore().handleException(
                        i18n.global.t('productprocessdefinition.detail.error.update.title'),
                        error
                    );
                    return false;
                } finally {
                    this.reloading = false;
                }
            },
            async create(body: ProductProcessDefinitionCreateDto) {
                this.loading = true;
                let response = null;
                try {
                    response = await client.create(body);
                } catch (error) {
                    useAlertStore().handleException(
                        i18n.global.t('productprocessdefinition.create.error.title'),
                        error,
                        false
                    );
                } finally {
                    this.loading = false;
                }
                return response;
            },
            async update(id: number, dto: ProductProcessDefinitionUpdateDto) {
                this.reloading = true;
                try {
                    await client.update(id, dto);
                    useAlertStore().setSuccess(i18n.global.t('productprocessdefinition.detail.success.update.title'));
                    await this.get(id, true);
                    return true;
                } catch (error) {
                    useAlertStore().handleException(
                        i18n.global.t('productprocessdefinition.detail.error.update.title'),
                        error,
                        false
                    );
                } finally {
                    useProductProcessDefinitionEditStore().closeEdit();
                    this.reloading = false;
                }
            },
            async delete(id: number): Promise<boolean> {
                this.reloading = true;
                try {
                    await client.delete(id);
                    useAlertStore().setSuccess(i18n.global.t('productprocessdefinition.detail.success.delete.title'));
                    return true;
                } catch (error) {
                    useAlertStore().handleException(
                        i18n.global.t('productprocessdefinition.detail.error.delete.title'),
                        error,
                        false
                    );
                } finally {
                    this.reloading = false;
                }
                return false;
            },
            async duplicate(
                id: number,
                {
                    duplicateDeliveryProcessDefinition = false,
                    duplicateDeliveryQualityChecks = false
                }: {
                    duplicateDeliveryProcessDefinition: boolean;
                    duplicateDeliveryQualityChecks: boolean;
                }
            ): Promise<number> {
                let response = null;
                try {
                    response = await client.duplicate(id, {
                        duplicateEntityModels: true,
                        duplicateDeliveryProcessDefinition,
                        duplicateDeliveryQualityChecks
                    });
                    useAlertStore().setSuccess(
                        i18n.global.t('productprocessdefinition.detail.success.duplicate.title')
                    );
                } catch (error) {
                    useAlertStore().handleException(
                        i18n.global.t('productprocessdefinition.detail.error.duplicate.title'),
                        error
                    );
                }
                return response?.id;
            },
            async export(id: number, exportOptions: ProductProcessDefinitionExportOptionsDto): Promise<void> {
                try {
                    const file = await client.export(id, exportOptions);

                    return new Promise((resolve) => {
                        downloadBlob(file.data, file.fileName, () => {
                            resolve();
                        });
                    });
                } catch (error) {
                    useAlertStore().handleException(
                        i18n.global.t('productprocessdefinition.detail.error.export.title'),
                        error,
                        false
                    );
                }
            },
            async import(file: File, parentDefinitionId: number = null): Promise<number> {
                let response = null;
                try {
                    response = await client.import(parentDefinitionId, { data: file, fileName: file.name });
                    useAlertStore().setSuccess(i18n.global.t('productprocessdefinition.detail.success.import.title'));
                } catch (error: any) {
                    useAlertStore().handleException(
                        i18n.global.t('productprocessdefinition.detail.error.import.title'),
                        error,
                        false
                    );
                }
                return response?.id;
            },
            async cascadeDelete(
                id: number,
                options: ProductProcessDefinitionCascadeDeleteOptionsDto = { deleteEntityModels: true }
            ) {
                this.reloading = true;
                try {
                    await client.cascadeDelete(id, options);
                    useAlertStore().setSuccess(i18n.global.t('productprocessdefinition.detail.success.delete.title'));
                    return true;
                } catch (error: any) {
                    useAlertStore().handleException(
                        i18n.global.t('productprocessdefinition.detail.error.delete.title'),
                        error
                    );
                } finally {
                    useProductProcessDefinitionEditStore().closeEdit();
                    this.reloading = false;
                }
            },
            async validateCascadeDelete(id: number): Promise<boolean> {
                try {
                    const isValid = await client.cascadeDeleteValidate(id);
                    return isValid;
                } catch (error) {
                    console.error('validateCascadeDelete', error);
                    return false;
                }
            },
            async importSpecification(id: number, importSpecification: ImportSpecificationDto) {
                this.reloading = true;

                try {
                    await client.importSpecification(id, importSpecification);

                    useAlertStore().setSuccess(
                        i18n.global.t('productprocessdefinition.detail.success.importSpecification.title')
                    );

                    if (importSpecification.target == ImportSpecificationType.ProductSpecification) {
                        await useProductSpecificationStore().getByDefinition(id, true);
                    }
                    if (importSpecification.target == ImportSpecificationType.SourceSpecification) {
                        await useSourceSpecificationStore().getByDefinition(id, true);
                    }
                } catch (error) {
                    useAlertStore().handleException(
                        i18n.global.t('productprocessdefinition.detail.error.importSpecification.title'),
                        error
                    );
                } finally {
                    useProductProcessDefinitionEditStore().closeEdit();
                    this.reloading = false;
                }
            },
            async generateReport(id: number, options: ProductProcessDefinitionReportingOptionsDto): Promise<void> {
                try {
                    const file = await client.generateReport(id, options);

                    return new Promise((resolve) => {
                        downloadBlob(file.data, file.fileName, () => {
                            resolve();
                        });
                    });
                } catch (error) {
                    useAlertStore().handleException(
                        i18n.global.t('productprocessdefinition.detail.error.generateReport.title'),
                        error
                    );
                }
            },
            clear(id: number) {
                this.detail = null;
                this.dependenciesReport = null;
                useSourceSpecificationStore().clear(id);
                useProductSpecificationStore().clear(id);
            }
        }
    }
);
